/**
 * Schema utility functions for Static Web Generator
 * Consolidates schema-related helper functions
 */

import {
  FieldSchema,
  FieldSchemaValue,
} from "../../../types/staticWebGenerator";

/**
 * Check if a value is a FieldSchemaValue (leaf node with required/value)
 */
export const isFieldSchemaValue = (
  value: FieldSchemaValue | FieldSchema | any,
): value is FieldSchemaValue => {
  return (
    typeof value === "object" &&
    value !== null &&
    "required" in value &&
    "value" in value
  );
};

/**
 * List of optional section schema keys
 */
export const OPTIONAL_SECTIONS = [
  "gallery",
  "testimonials",
  "service_highlights",
  "banner",
  "announcement_modal",
  "team",
] as const;

/**
 * Check if a schema key is an optional section
 */
export const isOptionalSection = (schemaKey: string): boolean => {
  return OPTIONAL_SECTIONS.includes(schemaKey as any);
};

/**
 * Map optional section enabled keys to schema keys
 * e.g., "highlights" → "service_highlights", "announcementBanner" → "announcement_modal"
 */
export const getOptionalSectionSchemaKey = (enabledKey: string): string => {
  const mapping: Record<string, string> = {
    highlights: "service_highlights",
    announcementBanner: "announcement_modal",
  };
  return mapping[enabledKey] || enabledKey;
};

/**
 * Map schema keys to optional section enabled keys
 * e.g., "service_highlights" → "highlights", "announcement_modal" → "announcementBanner"
 */
export const getOptionalSectionEnabledKey = (schemaKey: string): string => {
  const mapping: Record<string, string> = {
    service_highlights: "highlights",
    announcement_modal: "announcementBanner",
  };
  return mapping[schemaKey] || schemaKey;
};

/**
 * Get nested schema from a schema section
 * Handles both wrapped ({ required, value: {...} }) and plain nested formats
 */
export const getNestedSchema = (
  schemaValue: FieldSchemaValue | FieldSchema,
): FieldSchema | null => {
  if (isFieldSchemaValue(schemaValue)) {
    const maybeNested = schemaValue.value;
    if (
      maybeNested &&
      typeof maybeNested === "object" &&
      !Array.isArray(maybeNested)
    ) {
      return maybeNested as FieldSchema;
    }
    return null; // Leaf field, not nested
  }
  return schemaValue as FieldSchema;
};
